/** GPU Pinch — fragment-shader port of `lib/filters/pinch.ts`.
 *
 *  Radial r^exponent warp with three regions:
 *
 *  - r == 0: copy source center (undefined direction otherwise).
 *  - 0 < r < SMOOTH_INNER: full pinch warp, sample at r^exp scaled.
 *  - SMOOTH_INNER ≤ r < 1: smoothstep-blend warp ↔ source for a
 *    seamless boundary at the inscribed-half-diagonal circle.
 *  - r ≥ 1: pass-through clean source.
 *
 *  Matches the JS path's region boundaries + smoothing band
 *  (0.9..1.0) precisely so byte parity holds on the inner
 *  silhouette circle. Bilinear sampling via the runtime's
 *  `filter: 'linear'` option. */

import { PINCH_SMOOTH_INNER, type PinchValue } from '../../filters/pinch';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const PINCH_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_center;
uniform float u_halfDiag;
uniform float u_exponent;
out vec4 outColor;

// SMOOTH_INNER interpolated from the canonical CPU export so the
// GPU and JS paths share the band geometry exactly. SMOOTH_RANGE
// derives from it in-shader; keeping the relationship explicit
// avoids two interpolated constants going out of sync.
const float SMOOTH_INNER = ${PINCH_SMOOTH_INNER.toFixed(6)};
const float SMOOTH_RANGE = 1.0 - SMOOTH_INNER;

void main() {
  vec2 px = v_uv * u_dims - 0.5;
  vec2 d = (px - u_center) / u_halfDiag;
  float r = length(d);

  // r == 0: source center.
  if (r < 1e-6) {
    vec2 srcUv = (u_center + 0.5) / u_dims;
    outColor = texture(u_source, srcUv);
    return;
  }

  // r >= 1: pass-through. JS reads src[di..di+3] directly; we
  // sample at v_uv with NEAREST-equivalent UV (texel center).
  if (r >= 1.0) {
    outColor = texture(u_source, v_uv);
    return;
  }

  float rWarped = pow(r, u_exponent);
  float scale = rWarped / r;
  vec2 srcPx = u_center + d * scale * u_halfDiag;
  vec2 srcUv = (srcPx + 0.5) / u_dims;
  vec4 warped = texture(u_source, srcUv);

  if (r > SMOOTH_INNER) {
    // Smoothing band: lerp warped ↔ source via smoothstep.
    vec4 src = texture(u_source, v_uv);
    float t = (r - SMOOTH_INNER) / SMOOTH_RANGE;
    float blend = t * t * (3.0 - 2.0 * t);
    outColor = mix(warped, src, blend);
  } else {
    outColor = warped;
  }
}
`;

export function applyPinchOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: PinchValue,
): boolean {
  const amount = Math.max(-100, Math.min(100, value.amount));
  if (amount === 0) return applyIdentityCopy(source, target);
  // Same -100..+100 → exponent mapping as the JS path.
  const exponent = amount > 0 ? 1 + amount / 100 : 1 / (1 - amount / 100);
  const w = source.width;
  const h = source.height;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  return applyShader({
    source,
    target,
    fragShaderSrc: PINCH_FRAG,
    filter: 'linear',
    uniforms: {
      u_dims: { type: 'vec2', value: [w, h] },
      u_center: { type: 'vec2', value: [cx, cy] },
      u_halfDiag: { type: 'float', value: halfDiag },
      u_exponent: { type: 'float', value: exponent },
    },
  });
}
